import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createTextVNode as _createTextVNode, createBlock as _createBlock, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "flex flex-col items-center gap-2" };
const _hoisted_2 = { class: "flex flex-col items-center gap-0.5 text-center" };
const _hoisted_3 = ["id"];
const _hoisted_4 = ["id"];
const _hoisted_5 = { class: "flex flex-col gap-5" };
const _hoisted_6 = {
    key: 0,
    class: "flex flex-col items-center gap-2 text-center",
    "data-testid": "pseudonymise-blocked"
};
const _hoisted_7 = { class: "text-base leading-6 text-(--text-text-body)" };
const _hoisted_8 = {
    key: 1,
    class: "flex flex-col gap-3",
    "aria-busy": "true",
    "data-testid": "pseudonymise-loading"
};
const _hoisted_9 = { class: "sr-only" };
const _hoisted_10 = {
    key: 2,
    class: "flex flex-col items-center gap-2 text-center",
    "data-testid": "pseudonymise-cancelled"
};
const _hoisted_11 = { class: "text-base leading-6 font-[var(--weight-bold)] text-(--text-text-body)" };
const _hoisted_12 = { class: "text-base leading-6 text-(--text-text-body)" };
const _hoisted_13 = { class: "text-[length:var(--size-l)] text-(--text-text-body)" };
const _hoisted_14 = {
    class: "text-[length:var(--size-l)] text-(--text-text-body)",
    "data-testid": "pseudonymise-recap"
};
const _hoisted_15 = {
    key: 0,
    class: "text-[length:var(--size-m)] text-(--text-text-body-subtle)",
    "data-testid": "pseudonymise-failed"
};
const _hoisted_16 = {
    key: 1,
    class: "text-[length:var(--size-m)] text-(--text-text-body-subtle)",
    "data-testid": "pseudonymise-no-answer"
};
const _hoisted_17 = {
    key: 5,
    class: "flex flex-col gap-5",
    "data-testid": "pseudonymise-result"
};
const _hoisted_18 = {
    key: 6,
    role: "alert",
    "data-testid": "pseudonymise-error",
    class: "rounded-lg bg-(--background-background-error) border border-(--border-border-error-subtle) px-4 py-3 text-sm text-(--text-text-error) flex items-center gap-2"
};
const _hoisted_19 = {
    key: 7,
    role: "alert",
    "data-testid": "pseudonymise-polling-error",
    class: "rounded-lg bg-(--background-background-error) border border-(--border-border-error-subtle) px-4 py-3 text-sm text-(--text-text-error) flex items-center justify-between gap-2"
};
const _hoisted_20 = {
    class: "sr-only",
    "aria-live": "polite",
    "data-testid": "pseudonymise-live"
};
const _hoisted_21 = {
    key: 2,
    class: "flex items-center gap-5"
};
import { computed, onBeforeUnmount, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Alert, Button, FieldSkeleton, IconShape, Modal, Tooltip } from "@eudonet/opal";
import { cancelRequest, confirmRequest, getRequest, submitRequest } from "../../infrastructure/api/requestsApi";
import { isPermanentFailureStatus } from "../../infrastructure/api/httpStatus";
import { getApiErrorStatus } from "@/shared/utils/apiErrorStatus";
import { useRequestPolling } from "../../composables/useRequestPolling";
import { failedImpactServices, isTerminalStatus, isWaitingStatus, servicesToProcess, stepForStatus, timedOutImpactServices, totalFieldsToModify, } from "../../domain/request";
import { serviceLabel, useRequestTranslator } from "../requestLabels";
import RequestImpactList from "./RequestImpactList.vue";
import RequestResultList from "./RequestResultList.vue";
import { getApiErrorCode, getApiErrorResult } from "@/infrastructure/services/ApiErrorService";
import { logger } from "@/infrastructure/services/LoggerService";
const TOTAL_STEPS = 3;
const OPEN_REQUEST_EXISTS = "Request.Subject.OpenRequestExists";
const INVALID_TRANSITION = "Request.InvalidTransition";
export default /*@__PURE__*/ _defineComponent({
    __name: 'PseudonymiseContactModal',
    props: /*@__PURE__*/ _mergeModels({
        contactId: {},
        contactName: {}
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["completed"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const props = __props;
        const open = _useModel(__props, "modelValue");
        const emit = __emit;
        const PSEUDONYMISE_ERROR_OVERRIDES = {
            "Request.NothingToProcess": "compliance.pseudonymise.blocked.nothingToProcess",
            "Request.Subject.OwnerNotRegistered": "compliance.pseudonymise.blocked.ownerNotRegistered",
            "Request.Subject.AlreadyTreated": "compliance.pseudonymise.blocked.alreadyTreated",
        };
        const { t } = useI18n();
        const translator = useRequestTranslator();
        const titleId = useId();
        const subtitleId = useId();
        const request = ref(null);
        const wantsConfirmation = ref(false);
        const submitting = ref(null);
        const blockedKey = ref(null);
        const errorKey = ref(null);
        const pollingFailure = ref(null);
        const rereadFor = ref(null);
        const polling = useRequestPolling({
            fetchRequest: getRequest,
            isSettled: (current) => !isWaitingStatus(current.status),
            onUpdate: (current) => {
                request.value = current;
                pollingFailure.value = null;
                if (rereadFor.value) {
                    rereadFor.value = null;
                    wantsConfirmation.value = false;
                }
            },
            onError: (err) => {
                logger.error("Pseudonymisation request polling failed", err);
                rereadFor.value = null;
                const status = getApiErrorStatus(err);
                pollingFailure.value =
                    isPermanentFailureStatus(status)
                        ? { key: failureKeyFor(status), retryable: false }
                        : { key: "compliance.pseudonymise.errors.polling", retryable: true };
            },
        });
        const step = computed(() => {
            if (!request.value)
                return null;
            switch (stepForStatus(request.value.status)) {
                case "cancelled":
                    return "cancelled";
                case "result":
                    return 3;
                default:
                    return wantsConfirmation.value ? 2 : 1;
            }
        });
        const impactReady = computed(() => request.value?.status === "ImpactReady");
        const confirmBusy = computed(() => submitting.value === "confirm" || rereadFor.value === "confirm");
        const cancelBusy = computed(() => submitting.value === "cancel" || rereadFor.value === "cancel");
        const busy = computed(() => submitting.value !== null || rereadFor.value !== null);
        const stepLabel = computed(() => step.value === null || step.value === "cancelled" || blockedKey.value
            ? ""
            : t("compliance.pseudonymise.step", { current: step.value, total: TOTAL_STEPS }));
        const modalSize = computed(() => {
            if (blockedKey.value || step.value === "cancelled")
                return "xs";
            return step.value === 2 ? "sm" : "md";
        });
        const recapFields = computed(() => totalFieldsToModify(request.value));
        const noAnswerServices = computed(() => serviceLabels(timedOutImpactServices(request.value)));
        const failedServices = computed(() => serviceLabels(failedImpactServices(request.value)));
        const resultAlert = computed(() => {
            switch (request.value?.status) {
                case "Running":
                    return { color: "info", key: "compliance.pseudonymise.result.inProgress" };
                case "Completed":
                    return { color: "success", key: "compliance.pseudonymise.result.completed" };
                case "PartialFailure":
                    return { color: "warning", key: "compliance.pseudonymise.result.partialFailure" };
                default:
                    return { color: "default", key: "compliance.pseudonymise.result.unknownStatus" };
            }
        });
        const liveMessage = computed(() => {
            if (step.value === "cancelled")
                return t("compliance.pseudonymise.cancelled.heading");
            if (step.value === 1 && impactReady.value)
                return t("compliance.pseudonymise.preview.ready");
            if (step.value === 3 && request.value && isTerminalStatus(request.value.status))
                return t(resultAlert.value.key);
            return "";
        });
        // CONSTRAINT: bumped on every open, close and unmount; each async step compares it after its await,
        // so a response belonging to an earlier opening never writes into the current one.
        let opening = 0;
        watch(open, (isOpen, wasOpen) => {
            opening += 1;
            if (isOpen) {
                void begin(opening);
                return;
            }
            polling.stop();
            if (wasOpen && request.value && isTerminalStatus(request.value.status)) {
                emit("completed");
            }
        }, { immediate: true });
        watch(step, () => {
            errorKey.value = null;
        });
        onBeforeUnmount(() => {
            opening += 1;
            polling.stop();
        });
        function reset() {
            request.value = null;
            wantsConfirmation.value = false;
            blockedKey.value = null;
            errorKey.value = null;
            pollingFailure.value = null;
            rereadFor.value = null;
            submitting.value = null;
        }
        async function begin(current) {
            reset();
            try {
                const created = await submitRequest(props.contactId);
                if (current !== opening)
                    return;
                polling.start(created.requestId);
            }
            catch (err) {
                if (current !== opening)
                    return;
                const code = getApiErrorCode(err);
                const openRequestId = code === OPEN_REQUEST_EXISTS ? readRequestId(getApiErrorResult(err)) : null;
                if (openRequestId) {
                    polling.start(openRequestId);
                    return;
                }
                if (code && PSEUDONYMISE_ERROR_OVERRIDES[code]) {
                    blockedKey.value = PSEUDONYMISE_ERROR_OVERRIDES[code];
                    return;
                }
                logger.error("Pseudonymisation request submission failed", err);
                errorKey.value = failureKey(err);
            }
        }
        async function confirm() {
            const current = request.value;
            if (!current)
                return;
            const confirmOpening = opening;
            submitting.value = "confirm";
            errorKey.value = null;
            try {
                await confirmRequest(current.requestId);
                if (confirmOpening !== opening)
                    return;
                // The confirm answer carries the request status only: the targets' apply statuses arrive with the next read.
                rereadFor.value = "confirm";
                polling.start(current.requestId);
            }
            catch (err) {
                if (confirmOpening !== opening)
                    return;
                if (getApiErrorCode(err) === INVALID_TRANSITION) {
                    rereadFor.value = "confirm";
                    polling.start(current.requestId);
                    return;
                }
                logger.error("Pseudonymisation confirmation failed", err);
                errorKey.value = failureKey(err);
            }
            finally {
                submitting.value = null;
            }
        }
        async function cancel() {
            const current = request.value;
            if (!current || busy.value)
                return;
            const cancelOpening = opening;
            // CONSTRAINT: stop before the call, so a read already in flight cannot write ImpactPending back over Cancelled.
            polling.stop();
            submitting.value = "cancel";
            errorKey.value = null;
            pollingFailure.value = null;
            try {
                const cancelled = await cancelRequest(current.requestId);
                if (cancelOpening !== opening)
                    return;
                request.value = { ...current, status: cancelled.status };
                polling.start(current.requestId);
            }
            catch (err) {
                if (cancelOpening !== opening)
                    return;
                if (getApiErrorCode(err) === INVALID_TRANSITION) {
                    rereadFor.value = "cancel";
                    polling.start(current.requestId);
                    return;
                }
                logger.error("Pseudonymisation request cancellation failed", err);
                errorKey.value = failureKey(err);
                if (!isPermanentFailureStatus(getApiErrorStatus(err))) {
                    polling.start(current.requestId);
                }
            }
            finally {
                submitting.value = null;
            }
        }
        function serviceLabels(codes) {
            return codes.map((code) => serviceLabel(translator, code)).join(", ");
        }
        function goBack() {
            wantsConfirmation.value = false;
        }
        function retryPolling() {
            pollingFailure.value = null;
            polling.retry();
        }
        function readRequestId(result) {
            if (result !== null && typeof result === "object" && "requestId" in result && typeof result.requestId === "string") {
                return result.requestId;
            }
            return null;
        }
        function failureKey(err) {
            return failureKeyFor(getApiErrorStatus(err));
        }
        function failureKeyFor(status) {
            if (status === 403)
                return "compliance.pseudonymise.errors.forbidden";
            if (status === 404)
                return "compliance.pseudonymise.errors.notFound";
            if (status !== undefined && status >= 502 && status <= 504)
                return "compliance.pseudonymise.errors.unavailable";
            return "compliance.pseudonymise.errors.generic";
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: open.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((open).value = $event)),
                size: modalSize.value,
                "header-position": "center",
                "footer-position": "split",
                "show-close-button": !busy.value,
                dismissable: !busy.value,
                "close-on-escape": !busy.value,
                "show-header-border": false,
                "show-footer-border": false,
                "aria-labelledby": _unref(titleId),
                "aria-describedby": stepLabel.value ? _unref(subtitleId) : undefined
            }, {
                header: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _cache[3] || (_cache[3] = _createElementVNode("div", { class: "flex items-center justify-center w-12 h-12 rounded-lg border border-(--border-border-base-soft) bg-(--background-background-white)" }, [
                            _createElementVNode("i", {
                                class: "ri-shield-user-line text-2xl text-(--text-text-black-default)",
                                "aria-hidden": "true"
                            })
                        ], -1 /* CACHED */)),
                        _createElementVNode("div", _hoisted_2, [
                            _createElementVNode("h2", {
                                id: _unref(titleId),
                                class: "text-[length:var(--size-xxl)] font-[var(--weight-bold)]"
                            }, _toDisplayString(_unref(t)("compliance.pseudonymise.title", { name: __props.contactName })), 9 /* TEXT, PROPS */, _hoisted_3),
                            _createElementVNode("p", {
                                id: _unref(subtitleId),
                                class: "text-[length:var(--size-l)] text-(--text-text-body-subtle)",
                                "data-testid": "pseudonymise-step"
                            }, _toDisplayString(stepLabel.value), 9 /* TEXT, PROPS */, _hoisted_4)
                        ])
                    ])
                ]),
                footer: _withCtx(() => [
                    (step.value === 2 && !blockedKey.value)
                        ? (_openBlock(), _createBlock(_unref(Button), {
                            key: 0,
                            variant: "secondary",
                            label: _unref(t)('compliance.pseudonymise.actions.back'),
                            disabled: busy.value,
                            "data-testid": "pseudonymise-back",
                            onClick: goBack
                        }, null, 8 /* PROPS */, ["label", "disabled"]))
                        : (_openBlock(), _createBlock(_unref(Button), {
                            key: 1,
                            variant: "secondary",
                            label: _unref(t)('compliance.pseudonymise.actions.close'),
                            disabled: busy.value,
                            "data-testid": "pseudonymise-close",
                            onClick: _cache[0] || (_cache[0] = ($event) => (open.value = false))
                        }, null, 8 /* PROPS */, ["label", "disabled"])),
                    ((step.value === 1 || step.value === 2) && !blockedKey.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                            _createVNode(_unref(Button), {
                                variant: "error-secondary",
                                label: _unref(t)('compliance.pseudonymise.actions.cancelRequest'),
                                loading: cancelBusy.value,
                                disabled: busy.value,
                                "data-testid": "pseudonymise-cancel-request",
                                onClick: cancel
                            }, null, 8 /* PROPS */, ["label", "loading", "disabled"]),
                            (step.value === 2)
                                ? (_openBlock(), _createBlock(_unref(Button), {
                                    key: 0,
                                    variant: "error",
                                    "icon-left": "ri-shield-user-line",
                                    label: _unref(t)('compliance.pseudonymise.actions.confirm'),
                                    loading: confirmBusy.value,
                                    disabled: busy.value,
                                    "data-testid": "pseudonymise-confirm",
                                    onClick: confirm
                                }, null, 8 /* PROPS */, ["label", "loading", "disabled"]))
                                : (_openBlock(), _createBlock(_unref(Tooltip), {
                                    key: 1,
                                    text: _unref(t)('compliance.pseudonymise.preview.waiting'),
                                    disabled: impactReady.value,
                                    tabindex: impactReady.value ? undefined : 0,
                                    "data-testid": "pseudonymise-continue-tooltip"
                                }, {
                                    default: _withCtx(() => [
                                        _createVNode(_unref(Button), {
                                            variant: "primary",
                                            label: _unref(t)('compliance.pseudonymise.actions.continue'),
                                            disabled: !impactReady.value || busy.value,
                                            "data-testid": "pseudonymise-continue",
                                            onClick: _cache[1] || (_cache[1] = ($event) => (wantsConfirmation.value = true))
                                        }, null, 8 /* PROPS */, ["label", "disabled"])
                                    ]),
                                    _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["text", "disabled", "tabindex"]))
                        ]))
                        : _createCommentVNode("v-if", true)
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                        (blockedKey.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                _createVNode(_unref(IconShape), {
                                    size: "md",
                                    color: "error",
                                    variant: "outlined",
                                    icon: "ri-error-warning-line"
                                }),
                                _createElementVNode("p", _hoisted_7, _toDisplayString(_unref(t)(blockedKey.value)), 1 /* TEXT */)
                            ]))
                            : (step.value === null && !errorKey.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                    _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(t)("compliance.pseudonymise.loading")), 1 /* TEXT */),
                                    _createVNode(_unref(FieldSkeleton)),
                                    _createVNode(_unref(FieldSkeleton))
                                ]))
                                : (step.value === 'cancelled')
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                        _createVNode(_unref(IconShape), {
                                            size: "md",
                                            color: "default",
                                            variant: "outlined",
                                            icon: "ri-close-circle-line"
                                        }),
                                        _createElementVNode("p", _hoisted_11, _toDisplayString(_unref(t)("compliance.pseudonymise.cancelled.heading")), 1 /* TEXT */),
                                        _createElementVNode("p", _hoisted_12, _toDisplayString(_unref(t)("compliance.pseudonymise.cancelled.body")), 1 /* TEXT */)
                                    ]))
                                    : (step.value === 1)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                                            _createElementVNode("p", _hoisted_13, _toDisplayString(_unref(t)("compliance.pseudonymise.preview.intro")), 1 /* TEXT */),
                                            _createVNode(RequestImpactList, {
                                                targets: request.value?.targets ?? []
                                            }, null, 8 /* PROPS */, ["targets"]),
                                            _createVNode(_unref(Alert), {
                                                color: "info",
                                                heading: _unref(t)('compliance.pseudonymise.preview.closeOrCancel')
                                            }, null, 8 /* PROPS */, ["heading"])
                                        ], 64 /* STABLE_FRAGMENT */))
                                        : (step.value === 2)
                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                                                _createVNode(_unref(Alert), {
                                                    color: "warning",
                                                    heading: _unref(t)('compliance.pseudonymise.confirm.irreversible')
                                                }, null, 8 /* PROPS */, ["heading"]),
                                                _createElementVNode("p", _hoisted_14, _toDisplayString(_unref(t)("compliance.pseudonymise.confirm.recap", { fields: recapFields.value, services: _unref(servicesToProcess)(request.value) })), 1 /* TEXT */),
                                                (failedServices.value)
                                                    ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(_unref(t)("compliance.pseudonymise.confirm.failed", { services: failedServices.value })), 1 /* TEXT */))
                                                    : _createCommentVNode("v-if", true),
                                                (noAnswerServices.value)
                                                    ? (_openBlock(), _createElementBlock("p", _hoisted_16, _toDisplayString(_unref(t)("compliance.pseudonymise.confirm.noAnswer", { services: noAnswerServices.value })), 1 /* TEXT */))
                                                    : _createCommentVNode("v-if", true)
                                            ], 64 /* STABLE_FRAGMENT */))
                                            : (step.value === 3)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                                    _createVNode(_unref(Alert), {
                                                        color: resultAlert.value.color,
                                                        heading: _unref(t)(resultAlert.value.key)
                                                    }, null, 8 /* PROPS */, ["color", "heading"]),
                                                    _createVNode(RequestResultList, {
                                                        targets: request.value?.targets ?? []
                                                    }, null, 8 /* PROPS */, ["targets"])
                                                ]))
                                                : _createCommentVNode("v-if", true),
                        (errorKey.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                                _cache[4] || (_cache[4] = _createElementVNode("i", {
                                    class: "ri-error-warning-line text-base shrink-0",
                                    "aria-hidden": "true"
                                }, null, -1 /* CACHED */)),
                                _createTextVNode(" " + _toDisplayString(_unref(t)(errorKey.value)), 1 /* TEXT */)
                            ]))
                            : _createCommentVNode("v-if", true),
                        (pollingFailure.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                _createElementVNode("span", null, _toDisplayString(_unref(t)(pollingFailure.value.key)), 1 /* TEXT */),
                                (pollingFailure.value.retryable)
                                    ? (_openBlock(), _createBlock(_unref(Button), {
                                        key: 0,
                                        variant: "secondary",
                                        label: _unref(t)('compliance.pseudonymise.actions.retry'),
                                        "data-testid": "pseudonymise-retry",
                                        onClick: retryPolling
                                    }, null, 8 /* PROPS */, ["label"]))
                                    : _createCommentVNode("v-if", true)
                            ]))
                            : _createCommentVNode("v-if", true),
                        _createElementVNode("p", _hoisted_20, _toDisplayString(liveMessage.value), 1 /* TEXT */)
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "size", "show-close-button", "dismissable", "close-on-escape", "aria-labelledby", "aria-describedby"]));
        };
    }
});
